import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// ✅ Adjust this relative path depending on where you paste this file.
// Your image is at: frontend/src/assets/orange_v5.png
// If this file is in frontend/src/pages/FlappyBirdPage.jsx, then "../assets/..." is correct.
import birdSrc from "../assets/favicon_transparent.png";

type GameMode = "READY" | "RUNNING" | "GAMEOVER";

type Pipe = {
  x: number;
  gapTop: number;
  gapBot: number;
  scored: boolean;
};

export default function FlappyBirdPage() {
  const navigate = useNavigate();

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // -----------------------------
    // LocalStorage
    // -----------------------------
    const STORAGE_BEST = "flappy_best_score_v1";
    const STORAGE_LAST = "flappy_last_score_v1";

    const readNumber = (key: string, fallback = 0): number => {
      try {
        const v = Number(localStorage.getItem(key));
        return Number.isFinite(v) ? v : fallback;
      } catch {
        return fallback;
      }
    };

    const writeNumber = (key: string, value: number): void => {
      try {
        localStorage.setItem(key, String(value));
      } catch {
        // ignore (private mode / disabled storage)
      }
    };

    // -----------------------------
    // Assets
    // -----------------------------
    const birdImg = new Image();
    birdImg.decoding = "async";
    birdImg.src = birdSrc;

    // -----------------------------
    // Game state (kept out of React state for performance)
    // -----------------------------
    const game: {
      // sizing
      dpr: number;
      cssW: number;
      cssH: number;
      w: number;
      h: number;

      // computed config
      groundH: number;
      pipeW: number;
      pipeSpeed: number;
      pipeEvery: number;
      gapH: number;
      gravity: number;
      flapV: number;
      birdSize: number;

      // entities
      birdX: number;
      birdY: number;
      birdV: number;

      pipes: Pipe[];
      pipeStart: number;
      spawnT: number;

      // scoring
      score: number;
      best: number;
      lastScore: number;

      // state
      mode: GameMode;
      t: number;

      // loop
      raf: number;
      lastTs: number;
      accum: number;

      // polish
      shake: number;

      // caches
      skyGrad: CanvasGradient | null;
      skyGradH: number;

      // sizing lifecycle
      hasSized: boolean;
      lastLandscape: boolean;
    } = {
      // sizing
      dpr: 1,
      cssW: 0,
      cssH: 0,
      w: 0,
      h: 0,

      // computed config
      groundH: 0,
      pipeW: 0,
      pipeSpeed: 0,
      pipeEvery: 1.35,
      gapH: 0,
      gravity: 0,
      flapV: 0,
      birdSize: 0,

      // entities
      birdX: 0,
      birdY: 0,
      birdV: 0,

      pipes: [] as Pipe[],
      pipeStart: 0,
      spawnT: 0,

      // scoring
      score: 0,
      best: readNumber(STORAGE_BEST, 0),
      lastScore: readNumber(STORAGE_LAST, 0),

      // state
      mode: "READY",
      t: 0,

      // loop
      raf: 0,
      lastTs: 0,
      accum: 0,

      // polish
      shake: 0,

      // caches
      skyGrad: null,
      skyGradH: 0,

      // sizing lifecycle
      hasSized: false,
      lastLandscape: false,
    };

    // -----------------------------
    // Restart guarding (avoid instant restart after death)
    // -----------------------------
    let restartArmed = false;
    let restartArmTimer: number | null = null;
    const clearRestartArm = () => {
      restartArmed = false;
      if (restartArmTimer !== null) {
        window.clearTimeout(restartArmTimer);
        restartArmTimer = null;
      }
    };

    // -----------------------------
    // Helpers
    // -----------------------------
    const clamp = (v: number, lo: number, hi: number): number =>
      Math.max(lo, Math.min(hi, v));
    const rand = (a: number, b: number): number => a + Math.random() * (b - a);

    const circleRectCollide = (
      cx: number,
      cy: number,
      r: number,
      rx: number,
      ry: number,
      rw: number,
      rh: number
    ): boolean => {
      const closestX = Math.max(rx, Math.min(cx, rx + rw));
      const closestY = Math.max(ry, Math.min(cy, ry + rh));
      const dx = cx - closestX;
      const dy = cy - closestY;
      return dx * dx + dy * dy < r * r;
    };

    const computeCanvasSize = () => {
      // wrapper width (already constrained by CSS max-width)
      const wrapW = wrap.clientWidth || Math.min(window.innerWidth, 760);
      // visualViewport is more representative on mobile (less "jump" with browser chrome)
      const vh = window.visualViewport?.height || window.innerHeight || 800;
      const vw = window.innerWidth || wrapW;
      const isLandscape = vw > vh;

      // width: fill wrapper up to a nice max (CSS also caps)
      const cssW = Math.floor(wrapW);

      // height: large on portrait phones, but avoid eating the whole screen
      let cssH;
      if (isLandscape) {
        // landscape: height is limited; make game still usable
        cssH = Math.min(vh * 0.82, cssW * 0.92);
      } else {
        // portrait: big canvas, leave room for button below
        cssH = Math.min(vh * 0.74, cssW * 1.75);
      }

      cssH = clamp(cssH, 340, 860);

      const dpr = clamp(window.devicePixelRatio || 1, 1, 2);

      game.dpr = dpr;
      game.cssW = cssW;
      game.cssH = Math.floor(cssH);
      game.w = game.cssW;
      game.h = game.cssH;
      game.lastLandscape = isLandscape;

      canvas.style.width = `${game.cssW}px`;
      canvas.style.height = `${game.cssH}px`;
      canvas.width = Math.round(game.cssW * dpr);
      canvas.height = Math.round(game.cssH * dpr);

      // reset transform for crispness
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const recomputeConfig = () => {
      const { w, h } = game;

      // Ground
      game.groundH = clamp(h * 0.12, 54, 96);

      // Bird
      game.birdSize = clamp(h * 0.065, 26, 48);
      game.birdX = w * 0.28;

      // Physics tuned to feel good across sizes
      game.gravity = h * 3.2; // px/s^2
      game.flapV = -h * 1.08; // px/s (negative is up)

      // Pipes
      game.pipeW = clamp(w * 0.16, 52, 92);
      game.pipeSpeed = clamp(w * 0.55, 180, 340);
      game.gapH = clamp(h * 0.28, 130, 240);

      // Pipe spawn interval also slightly size-aware (optional)
      game.pipeEvery = clamp(1.25 + (520 - w) * 0.0004, 1.15, 1.45);
    };

    const resetRound = () => {
      clearRestartArm();
      recomputeConfig();

      game.birdY = game.h * 0.45;
      game.birdV = 0;

      game.pipes = [];
      game.pipeStart = 0;
      game.spawnT = 0;

      game.score = 0;
      game.mode = "READY";
      game.t = 0;

      game.shake = 0;
      game.accum = 0;
    };

    const spawnPipe = () => {
      const { w, h, groundH, gapH, pipeW } = game;

      const margin = 10;
      const minCenter = margin + gapH * 0.5;
      const maxCenter = h - groundH - margin - gapH * 0.5;

      const centerY = rand(minCenter, maxCenter);
      const gapTop = centerY - gapH / 2;
      const gapBot = centerY + gapH / 2;

      game.pipes.push({
        x: w + pipeW + 2,
        gapTop,
        gapBot,
        scored: false,
      });
    };

    const setGameOver = () => {
      clearRestartArm();
      game.mode = "GAMEOVER";
      game.shake = 0.22;

      game.lastScore = game.score;
      writeNumber(STORAGE_LAST, game.lastScore);

      if (game.score > game.best) {
        game.best = game.score;
        writeNumber(STORAGE_BEST, game.best);
      }
    };

    const flapOrStart = () => {
      // This is called on tap/click/space
      if (game.mode === "READY") {
        clearRestartArm();
        game.mode = "RUNNING";
        game.birdV = game.flapV;
        return;
      }
      if (game.mode === "RUNNING") {
        clearRestartArm();
        game.birdV = game.flapV;
        return;
      }
      if (game.mode === "GAMEOVER") {
        // Require a second tap/click to restart so a quick post-death tap
        // doesn't instantly relaunch the round (user may miss the death).
        if (!restartArmed) {
          restartArmed = true;
          if (restartArmTimer !== null) window.clearTimeout(restartArmTimer);
          restartArmTimer = window.setTimeout(() => {
            restartArmed = false;
            restartArmTimer = null;
          }, 1400);
          return;
        }
        clearRestartArm();
        resetRound();
        game.mode = "RUNNING";
        game.birdV = game.flapV;
      }
    };

    // -----------------------------
    // Update & Render
    // -----------------------------
    const update = (dt: number) => {
      game.t += dt;

      if (game.shake > 0) {
        game.shake = Math.max(0, game.shake - dt * 1.6);
      }

      if (game.mode !== "RUNNING") {
        // idle bob in READY / GAMEOVER
        if (game.mode === "READY") {
          const baseY = game.h * 0.45;
          game.birdY = baseY + Math.sin(game.t * 2.2) * (game.birdSize * 0.18);
        }
        return;
      }

      // Bird physics
      game.birdV += game.gravity * dt;
      game.birdY += game.birdV * dt;

      // Spawn pipes
      game.spawnT += dt;
      while (game.spawnT >= game.pipeEvery) {
        game.spawnT -= game.pipeEvery;
        spawnPipe();
      }

      // Move pipes
      for (let i = game.pipeStart; i < game.pipes.length; i++) {
        const p = game.pipes[i];
        p.x -= game.pipeSpeed * dt;
      }

      // Remove offscreen pipes
      while (
        game.pipeStart < game.pipes.length &&
        game.pipes[game.pipeStart].x + game.pipeW < -20
      ) {
        game.pipeStart++;
      }
      if (game.pipeStart > 32) {
        game.pipes = game.pipes.slice(game.pipeStart);
        game.pipeStart = 0;
      }

      // Score when passing pipe
      for (let i = game.pipeStart; i < game.pipes.length; i++) {
        const p = game.pipes[i];
        if (!p.scored && p.x + game.pipeW < game.birdX) {
          p.scored = true;
          game.score += 1;
        }
      }

      // Collisions
      const r = game.birdSize * 0.42;
      const cx = game.birdX;
      const cy = game.birdY;

      // Ceiling
      if (cy - r <= 0) {
        setGameOver();
        return;
      }

      // Ground
      if (cy + r >= game.h - game.groundH) {
        setGameOver();
        return;
      }

      // Pipes
      for (let i = game.pipeStart; i < game.pipes.length; i++) {
        const p = game.pipes[i];
        const rx = p.x;
        const rw = game.pipeW;

        // top pipe rect: (rx, 0, rw, gapTop)
        if (circleRectCollide(cx, cy, r, rx, 0, rw, p.gapTop)) {
          setGameOver();
          return;
        }

        // bottom pipe rect: (rx, gapBot, rw, h-groundH-gapBot)
        if (
          circleRectCollide(
            cx,
            cy,
            r,
            rx,
            p.gapBot,
            rw,
            game.h - game.groundH - p.gapBot
          )
        ) {
          setGameOver();
          return;
        }
      }
    };

    const drawBackground = () => {
      const { w, h } = game;

      if (!game.skyGrad || game.skyGradH !== h) {
        const sky = ctx.createLinearGradient(0, 0, 0, h);
        sky.addColorStop(0, "#7fd7ff");
        sky.addColorStop(1, "#e9fbff");
        game.skyGrad = sky;
        game.skyGradH = h;
      }
      ctx.fillStyle = game.skyGrad;
      ctx.fillRect(0, 0, w, h);

      // Subtle clouds (cheap circles)
      ctx.save();
      ctx.globalAlpha = 0.32;
      const t = game.t;
      const cloudCount = 6;
      for (let i = 0; i < cloudCount; i++) {
        const y = h * 0.12 + i * (h * 0.09);
        const x = ((t * 18 + i * 180) % (w + 220)) - 220;
        const s = 22 + i * 3;
        ctx.beginPath();
        ctx.arc(x, y, s, 0, Math.PI * 2);
        ctx.arc(x + s * 0.9, y + s * 0.15, s * 0.9, 0, Math.PI * 2);
        ctx.arc(x + s * 1.7, y, s * 0.75, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
      }
      ctx.restore();
    };

    const drawPipes = () => {
      const { pipeW, groundH, h } = game;

      // Flat fill + highlight strip (much cheaper than gradients per pipe).
      for (let i = game.pipeStart; i < game.pipes.length; i++) {
        const p = game.pipes[i];
        const x = p.x;

        // pipe main
        ctx.fillStyle = "#2ecc71";

        // Top pipe
        ctx.fillRect(x, 0, pipeW, p.gapTop);

        // Bottom pipe
        ctx.fillRect(x, p.gapBot, pipeW, h - groundH - p.gapBot);

        // Pipe caps (a bit wider)
        const capH = Math.max(10, game.birdSize * 0.35);
        const capW = pipeW * 1.08;
        const capX = x - (capW - pipeW) / 2;

        ctx.fillStyle = "#1e8f4d";
        ctx.fillRect(capX, Math.max(0, p.gapTop - capH), capW, capH);
        ctx.fillRect(capX, p.gapBot, capW, capH);

        // inner highlight
        ctx.save();
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(x + pipeW * 0.12, 0, pipeW * 0.12, p.gapTop);
        ctx.fillRect(
          x + pipeW * 0.12,
          p.gapBot,
          pipeW * 0.12,
          h - groundH - p.gapBot
        );
        ctx.restore();
      }
    };

    const drawGround = () => {
      const { w, h, groundH } = game;

      // grass
      ctx.fillStyle = "#46c36f";
      ctx.fillRect(0, h - groundH, w, groundH);

      // dirt
      ctx.fillStyle = "#b07a3f";
      ctx.fillRect(0, h - groundH * 0.55, w, groundH * 0.55);

      // scrolling stripes
      ctx.save();
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = "#000000";
      const stripeW = Math.max(18, w * 0.05);
      const offset = (game.t * game.pipeSpeed * 0.35) % (stripeW * 2);
      for (let x = -offset; x < w + stripeW * 2; x += stripeW * 2) {
        ctx.fillRect(x, h - groundH * 0.42, stripeW, groundH * 0.18);
      }
      ctx.restore();
    };

    const drawBird = () => {
      const { birdX, birdY, birdSize } = game;

      const angle = clamp(game.birdV / 900, -0.65, 0.85); // tilt based on velocity
      const half = birdSize / 2;

      ctx.save();
      ctx.translate(birdX, birdY);
      ctx.rotate(angle);

      if (birdImg.complete && birdImg.naturalWidth > 0) {
        ctx.drawImage(birdImg, -half, -half, birdSize, birdSize);
      } else {
        // fallback if image not loaded
        ctx.fillStyle = "#ff8a00";
        ctx.beginPath();
        ctx.arc(0, 0, half * 0.9, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    };

    const drawHUD = () => {
      const { w } = game;

      // Score top-center
      ctx.save();
      ctx.font =
        "700 22px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";

      // shadow
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.fillText(String(game.score), w / 2 + 1, 14 + 1);

      ctx.fillStyle = "#ffffff";
      ctx.fillText(String(game.score), w / 2, 14);

      ctx.restore();
    };

    const drawOverlay = () => {
      const { w, h } = game;

      const boxW = clamp(w * 0.82, 280, 460);
      const boxH = 170;
      const x = (w - boxW) / 2;
      const y = h * 0.18;

      ctx.save();
      // dim background
      ctx.fillStyle = "rgba(0,0,0,0.16)";
      ctx.fillRect(0, 0, w, h);

      // card
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      ctx.strokeStyle = "rgba(0,0,0,0.10)";
      ctx.lineWidth = 2;
      roundRect(ctx, x, y, boxW, boxH, 16);
      ctx.fill();
      ctx.stroke();

      ctx.textAlign = "center";
      ctx.fillStyle = "#0f172a";

      ctx.font =
        "800 20px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial";
      const title = game.mode === "READY" ? "Flappy Smagly" : "Game Over";
      ctx.fillText(title, w / 2, y + 26);

      ctx.font =
        "600 14px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial";
      const sub =
        game.mode === "READY"
          ? "Tryk / klik / Mellemrum for at starte"
          : restartArmed
          ? "Tryk / klik igen for at spille igen"
          : "Tryk / klik / Mellemrum for at spille igen";
      ctx.fillStyle = "#334155";
      ctx.fillText(sub, w / 2, y + 48);

      // Stats
      ctx.fillStyle = "#0f172a";
      ctx.font =
        "700 14px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial";

      const leftX = x + boxW * 0.22;
      const rightX = x + boxW * 0.78;
      const statY = y + 86;

      ctx.textAlign = "center";
      ctx.fillText(`Bedste: ${game.best}`, leftX, statY);
      ctx.fillText(`Sidste: ${game.lastScore}`, rightX, statY);

      // Tip
      ctx.fillStyle = "#475569";
      ctx.font =
        "600 12px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial";
      ctx.fillText("Tip: Hold en jævn tempo — Ikke spam jump", w / 2, y + 122);

      ctx.restore();
    };

    const roundRect = (
      c: CanvasRenderingContext2D,
      x: number,
      y: number,
      w: number,
      h: number,
      r: number
    ) => {
      const rr = Math.min(r, w / 2, h / 2);
      c.beginPath();
      c.moveTo(x + rr, y);
      c.arcTo(x + w, y, x + w, y + h, rr);
      c.arcTo(x + w, y + h, x, y + h, rr);
      c.arcTo(x, y + h, x, y, rr);
      c.arcTo(x, y, x + w, y, rr);
      c.closePath();
    };

    const render = () => {
      // Optional screen shake
      ctx.setTransform(game.dpr, 0, 0, game.dpr, 0, 0);
      if (game.shake > 0) {
        const s = game.shake * 10;
        const dx = (Math.random() - 0.5) * s;
        const dy = (Math.random() - 0.5) * s;
        // dx/dy in CSS pixels (correct regardless of DPR)
        ctx.translate(dx, dy);
      }

      drawBackground();
      drawPipes();
      drawGround();
      drawBird();
      drawHUD();

      if (game.mode === "READY" || game.mode === "GAMEOVER") {
        drawOverlay();
      }
    };

    // -----------------------------
    // Main loop
    // -----------------------------
    const tick = (ts: number) => {
      if (!game.lastTs) game.lastTs = ts;
      // Frame dt can spike (tab background / low-end phones). We cap it, then
      // use a fixed-timestep accumulator for consistent simulation speed.
      const frameDt = clamp((ts - game.lastTs) / 1000, 0, 0.1);
      game.lastTs = ts;

      const fixedDt = 1 / 60;
      game.accum += frameDt;

      let steps = 0;
      const maxSteps = 6; // prevent spiral of death
      while (game.accum >= fixedDt && steps < maxSteps) {
        update(fixedDt);
        game.accum -= fixedDt;
        steps++;
      }
      if (steps === maxSteps) {
        // If we're too far behind, drop accumulated time (keeps gameplay responsive).
        game.accum = 0;
      }

      render();

      game.raf = requestAnimationFrame(tick);
    };

    // -----------------------------
    // Events
    // -----------------------------
    const onPointerDown = (e: PointerEvent) => {
      // prevent the page from interpreting taps as scroll/zoom gestures on the canvas
      e.preventDefault();
      flapOrStart();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "ArrowUp" || e.code === "KeyW") {
        e.preventDefault();
        flapOrStart();
      }
      if (e.code === "KeyR") {
        e.preventDefault();
        resetRound();
      }
    };

    const onVisibility = () => {
      // If the tab is hidden, stop the bird from "falling" due to big dt when returning.
      game.lastTs = 0;
      game.accum = 0;
    };

    const doResize = () => {
      const prevW = game.w;
      const prevH = game.h;

      computeCanvasSize();
      recomputeConfig();

      // First time: initialize round state once sizes are known
      if (!game.hasSized) {
        game.hasSized = true;
        resetRound();
        return;
      }

      // Avoid wiping an active run for tiny mobile address-bar resizes.
      // Only reset on "material" changes (orientation switch / big size jump).
      const dW = Math.abs(game.w - prevW);
      const dH = Math.abs(game.h - prevH);
      const relW = prevW ? dW / prevW : 1;
      const relH = prevH ? dH / prevH : 1;

      const vw = window.innerWidth || game.w;
      const vh = window.visualViewport?.height || window.innerHeight || game.h;
      const isLandscape = vw > vh;
      const orientationChanged = isLandscape !== game.lastLandscape;
      game.lastLandscape = isLandscape;

      const material = orientationChanged || relW > 0.18 || relH > 0.18;
      if (material) {
        resetRound();
        return;
      }

      // Minor resize: preserve positions by scaling.
      const sx = prevW ? game.w / prevW : 1;
      const sy = prevH ? game.h / prevH : 1;

      game.birdY *= sy;
      game.birdY = clamp(
        game.birdY,
        game.birdSize * 0.6,
        game.h - game.groundH - game.birdSize * 0.6
      );

      for (let i = game.pipeStart; i < game.pipes.length; i++) {
        const p = game.pipes[i];
        p.x *= sx;
        p.gapTop *= sy;
        p.gapBot *= sy;
      }
    };

    // Use ResizeObserver when available so it adapts to layout changes in React
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => doResize());
      ro.observe(wrap);
    }

    // Initial sizing + start loop
    doResize();

    canvas.addEventListener("pointerdown", onPointerDown, { passive: false });
    window.addEventListener("keydown", onKeyDown, { passive: false });
    window.addEventListener("resize", doResize, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    game.raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(game.raf);
      canvas.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", doResize);
      document.removeEventListener("visibilitychange", onVisibility);
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <div className="fb-page">
      <div className="fb-shell" ref={wrapRef}>
        <canvas
          ref={canvasRef}
          className="fb-canvas"
          role="img"
          aria-label="Flappy Bird game canvas"
        />
        <div className="fb-hint" aria-hidden="true">
          Tryk / klik / Mellemrum
        </div>
      </div>

      {/* As requested: button under the game canvas */}
      <div className="fb-menu">
        <button className="fb-back" type="button" onClick={() => navigate("/")}>
          Tilbage til menuen
        </button>
      </div>

      {/* Single-file styling */}
      <style>{`
        .fb-page{
          min-height: 100vh;
          min-height: 100svh;
          min-height: 100dvh;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 14px 12px 18px;
          background: radial-gradient(1000px 600px at 50% 0%, rgba(255,255,255,0.10), rgba(255,255,255,0) 60%), #0b1220;
        }

        .fb-shell{
          width: 100%;
          max-width: 760px;
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
        }

        .fb-canvas{
          display: block;
          width: 100%;
          height: auto;
          border-radius: 16px;
          box-shadow:
            0 16px 40px rgba(0,0,0,0.35),
            0 2px 0 rgba(255,255,255,0.08) inset;
          outline: 1px solid rgba(255,255,255,0.08);
          background: transparent;

          /* important for mobile: tapping won’t scroll the page */
          touch-action: none;
          user-select: none;
          -webkit-user-select: none;
        }

        .fb-hint{
          position: absolute;
          left: 10px;
          bottom: 10px;
          padding: 6px 10px;
          font: 600 12px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
          color: rgba(255,255,255,0.9);
          background: rgba(0,0,0,0.20);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 999px;
          pointer-events: none;
          backdrop-filter: blur(6px);
        }

        .fb-menu{
          width: 100%;
          max-width: 760px;
        }

        .fb-back{
          width: 100%;
          border: 0;
          border-radius: 12px;
          padding: 12px 14px;
          font: 700 14px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
          color: #0b1220;
          background: #ffffff;
          box-shadow:
            0 10px 22px rgba(0,0,0,0.28),
            0 1px 0 rgba(255,255,255,0.25) inset;
          cursor: pointer;
        }

        .fb-back:active{
          transform: translateY(1px);
        }

        @media (max-width: 420px){
          .fb-page{
            gap: 10px;
            padding: 10px 10px 14px;
          }
        }
      `}</style>
    </div>
  );
}
